import { apiClient } from './apiClient';

const BOOKMARKS_API = '/api/app/bookmarks-extn';

export interface BookmarkDto {
  id: string;
  bookId: string;
  userId: string;
  location: string;
  locationLabel: string;
  note?: string;
  creationTime: string;
  concurrencyStamp?: string;
}

export interface BookmarkCreateDto {
  bookId: string;
  location: string;
  locationLabel: string;
  note?: string;
}

export interface BookmarkUpdateDto {
  location: string;
  locationLabel: string;
  note?: string;
  bookId: string;
  userId: string;
  concurrencyStamp: string;
}

export const bookmarksService = {
  /**
   * Get all bookmarks for current user and specific book
   */
  async getMyBookmarks(bookId: string): Promise<BookmarkDto[]> {
    const response = await apiClient.get<BookmarkDto[]>(
      `${BOOKMARKS_API}/my-bookmarks`,
      { params: { bookId } }
    );
    return response.data;
  },

  /**
   * Create a bookmark for current user
   */
  async createMyBookmark(input: BookmarkCreateDto): Promise<BookmarkDto> {
    const response = await apiClient.post<BookmarkDto>(
      `${BOOKMARKS_API}/my-bookmark`,
      input
    );
    return response.data;
  },

  /**
   * Update a bookmark (current user only)
   * Fetches current bookmark first to get concurrencyStamp
   */
  async updateMyBookmark(
    id: string,
    bookId: string,
    input: Partial<Omit<BookmarkUpdateDto, 'bookId' | 'userId' | 'concurrencyStamp'>>
  ): Promise<BookmarkDto> {
    // Fetch all bookmarks to find the one we're updating
    const bookmarks = await this.getMyBookmarks(bookId);
    const currentBookmark = bookmarks.find((b) => b.id === id);

    if (!currentBookmark) {
      throw new Error(`Bookmark with id ${id} not found`);
    }

    // Merge with update data
    const updateDto: BookmarkUpdateDto = {
      location: input.location || currentBookmark.location,
      locationLabel: input.locationLabel || currentBookmark.locationLabel,
      note: input.note !== undefined ? input.note : currentBookmark.note,
      bookId: currentBookmark.bookId,
      userId: currentBookmark.userId,
      concurrencyStamp: currentBookmark.concurrencyStamp || '',
    };

    const response = await apiClient.put<BookmarkDto>(
      `${BOOKMARKS_API}/my-bookmark/${id}`,
      updateDto
    );
    return response.data;
  },

  /**
   * Delete a bookmark (current user only)
   */
  async deleteMyBookmark(id: string): Promise<void> {
    await apiClient.delete(`${BOOKMARKS_API}/my-bookmark/${id}`);
  },

  /**
   * Check if a location is bookmarked
   */
  async isBookmarked(bookId: string, location: string): Promise<boolean> {
    const response = await apiClient.get<boolean>(
      `${BOOKMARKS_API}/is-bookmarked`,
      { params: { bookId, location } }
    );
    return response.data;
  },
};
